import { orderDisplayNumber } from '../utils/orderDisplay';

/**
 * Сервис уведомлений для баристы:
 * - Системные уведомления браузера (видны при свёрнутой вкладке)
 * - Звук: сначала файл из public/sounds/, при недоступности play() — громкий синтетический сигнал
 *
 * Файлы: public/sounds/new-order.mp3 и public/sounds/time.mp3
 * В киоске обязательно вызвать unlockNotificationAudio() из жеста пользователя.
 */

const NOTIFICATION_TAG = 'RCoffee-new-order';
const NOTIFICATION_TAG_PREPARE = 'RCoffee-time-to-prepare';

const base = process.env.PUBLIC_URL || '';
const SOUND_NEW_ORDER = `${base}/sounds/new-order.mp3`;
const SOUND_TIME_TO_PREPARE = `${base}/sounds/time.mp3`;

/** @type {Map<string, HTMLAudioElement>} */
const audioPreloadCache = new Map();

/** Разблокировка после жеста пользователя (без этого браузер часто блокирует play() при SSE). */
let mediaPlaybackUnlocked = false;

/** Общий контекст для синтетического звука и resume после sleep киоска. */
let sharedAudioContext = null;

function getPreloadedAudio(url) {
  let el = audioPreloadCache.get(url);
  if (!el) {
    el = new Audio(url);
    el.preload = 'auto';
    audioPreloadCache.set(url, el);
  }
  return el;
}

function getAudioContextClass() {
  return window.AudioContext || window.webkitAudioContext || null;
}

async function ensureAudioContextRunning() {
  const AC = getAudioContextClass();
  if (!AC) {
    return null;
  }
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new AC();
  }
  if (sharedAudioContext.state === 'suspended') {
    try {
      await sharedAudioContext.resume();
    } catch (e) {
      console.warn('[Notifications] AudioContext.resume():', e);
    }
  }
  return sharedAudioContext;
}

/**
 * Громкий «тревожный» тон: square + гармоника, высокий gain.
 * Square слышен в зале заметно лучше, чем мягкий sine.
 */
function scheduleLoudBeep(ctx, startTime, frequency, duration = 0.28, peakGain = 0.95) {
  const master = ctx.createGain();
  master.connect(ctx.destination);
  master.gain.setValueAtTime(0.0001, startTime);
  master.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.012);
  master.gain.setValueAtTime(peakGain, startTime + duration * 0.55);
  master.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  const fund = ctx.createOscillator();
  fund.type = 'square';
  fund.frequency.value = frequency;
  fund.connect(master);
  fund.start(startTime);
  fund.stop(startTime + duration);

  const harm = ctx.createOscillator();
  harm.type = 'square';
  harm.frequency.value = frequency * 2;
  const harmGain = ctx.createGain();
  harmGain.gain.value = 0.45;
  harm.connect(harmGain);
  harmGain.connect(master);
  harm.start(startTime);
  harm.stop(startTime + duration);
}

function runWhenContextReady(ctx, run) {
  if (!ctx) {
    return;
  }
  if (ctx.state === 'suspended') {
    ctx.resume().then(run).catch((e) => {
      console.warn('[Notifications] resume перед синтетикой:', e);
    });
  } else {
    run();
  }
}

/** Двойной громкий бип — новый заказ */
function playSyntheticNewOrderSound() {
  try {
    const AC = getAudioContextClass();
    if (!AC) {
      return;
    }
    const ctx =
      sharedAudioContext && sharedAudioContext.state !== 'closed'
        ? sharedAudioContext
        : new AC();
    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = ctx;
    }
    const now = () => ctx.currentTime;
    const run = () => {
      const t0 = now();
      // Три коротких удара — сложнее пропустить в шуме кофейни
      scheduleLoudBeep(ctx, t0, 880, 0.32, 0.98);
      scheduleLoudBeep(ctx, t0 + 0.38, 1040, 0.32, 0.98);
      scheduleLoudBeep(ctx, t0 + 0.76, 880, 0.4, 1);
    };
    runWhenContextReady(ctx, run);
  } catch (e) {
    console.warn('[Notifications] Синтетический звук недоступен:', e);
  }
}

/** Восходящая тройка — пора готовить */
function playSyntheticTimeToPrepareSound() {
  try {
    const AC = getAudioContextClass();
    if (!AC) {
      return;
    }
    const ctx =
      sharedAudioContext && sharedAudioContext.state !== 'closed'
        ? sharedAudioContext
        : new AC();
    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = ctx;
    }
    const now = () => ctx.currentTime;
    const run = () => {
      const t0 = now();
      scheduleLoudBeep(ctx, t0, 740, 0.3, 0.98);
      scheduleLoudBeep(ctx, t0 + 0.36, 980, 0.3, 0.98);
      scheduleLoudBeep(ctx, t0 + 0.72, 1240, 0.45, 1);
    };
    runWhenContextReady(ctx, run);
  } catch (e) {
    console.warn('[Notifications] Синтетический звук (пора готовить):', e);
  }
}

/**
 * Вызвать из обработчика жеста (клик «Войти», тап на доске).
 * force=true — повторно «разбудить» HTMLAudio после sleep киоска (visibility).
 */
export async function unlockNotificationAudio(force = false) {
  const ctx = await ensureAudioContextRunning();
  const ctxOk = Boolean(ctx && ctx.state === 'running');

  // Уже разблокировано и не force — достаточно resume context
  if (mediaPlaybackUnlocked && !force) {
    return true;
  }

  let htmlOk = false;

  const touchAudio = async (url) => {
    try {
      const audio = getPreloadedAudio(url);
      const prevVolume = audio.volume;
      audio.volume = 0.01;
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVolume;
      return true;
    } catch (e) {
      console.warn('[Notifications] Разблокировка HTMLAudio:', url, e);
      return false;
    }
  };

  const needHtmlTouch = force || !mediaPlaybackUnlocked;
  if (needHtmlTouch) {
    const a = await touchAudio(SOUND_NEW_ORDER);
    const b = await touchAudio(SOUND_TIME_TO_PREPARE);
    htmlOk = a || b;
  }

  if (!htmlOk && ctxOk && !mediaPlaybackUnlocked) {
    try {
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.05);
    } catch (_) {
      /* ignore */
    }
  }

  mediaPlaybackUnlocked = htmlOk || ctxOk || mediaPlaybackUnlocked;
  if (!mediaPlaybackUnlocked) {
    console.warn('[Notifications] unlock не удался — звук по SSE может не играть до следующего жеста');
  }
  return mediaPlaybackUnlocked;
}

export function isNotificationAudioUnlocked() {
  return mediaPlaybackUnlocked;
}

/**
 * @param {string} url
 * @param {number} [volume]
 * @param {() => void} [onFallback]
 */
async function playAudioFile(url, volume = 1, onFallback) {
  await ensureAudioContextRunning();
  try {
    const audio = getPreloadedAudio(url);
    if (audio.error) {
      console.warn('[Notifications] Файл звука недоступен (проверьте public/sounds):', url, audio.error);
      onFallback?.();
      return;
    }
    audio.volume = Math.min(1, Math.max(0, volume));
    audio.currentTime = 0;
    const promise = audio.play();
    if (promise !== undefined) {
      await promise.catch((err) => {
        console.warn('[Notifications] play() отклонён, запасной сигнал:', url, err);
        mediaPlaybackUnlocked = false;
        onFallback?.();
      });
    }
  } catch (e) {
    console.warn('[Notifications] Ошибка Audio:', e);
    mediaPlaybackUnlocked = false;
    onFallback?.();
  }
}

/** Отдельный экземпляр Audio на каждый вызов — надёжнее для отложенного сигнала */
async function playAudioFileOneShot(url, volume = 1, onFallback) {
  await ensureAudioContextRunning();
  try {
    const audio = new Audio(url);
    audio.volume = Math.min(1, Math.max(0, volume));
    const promise = audio.play();
    if (promise !== undefined) {
      await promise.catch((err) => {
        console.warn('[Notifications] one-shot play() отклонён:', url, err);
        mediaPlaybackUnlocked = false;
        onFallback?.();
      });
    }
  } catch (e) {
    console.warn('[Notifications] one-shot Audio:', e);
    mediaPlaybackUnlocked = false;
    onFallback?.();
  }
}

/**
 * Запрос разрешения на уведомления (вызывать после входа баристы).
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('[Notifications] Browser does not support Notification API');
    return 'denied';
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  try {
    const permission = await Notification.requestPermission();
    console.log('[Notifications] Permission:', permission);
    return permission;
  } catch (e) {
    console.error('[Notifications] Request permission error:', e);
    return 'denied';
  }
}

/**
 * Показать системное уведомление о новом заказе (работает при свёрнутой вкладке).
 * @param {{ id: number, items?: Array<{ item_name?: string }> }} order
 */
export function showNewOrderNotification(order) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  const title = `Новый заказ #${orderDisplayNumber(order)}`;
  const itemNames = (order.items || [])
    .slice(0, 3)
    .map((item) => item.item_name || 'Позиция')
    .join(', ');
  const body = itemNames ? `Состав: ${itemNames}${(order.items?.length || 0) > 3 ? '...' : ''}` : 'Откройте вкладку для просмотра';

  try {
    const notification = new Notification(title, {
      body,
      tag: NOTIFICATION_TAG,
      requireInteraction: true,
      icon: '/favicon.ico',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (e) {
    console.error('[Notifications] Show notification error:', e);
  }
}

/**
 * Звук о новом заказе — файл, иначе громкая синтетика.
 */
export function playNewOrderSound() {
  void playAudioFile(SOUND_NEW_ORDER, 1, playSyntheticNewOrderSound);
}

/**
 * Звук «пора готовить» — файл, иначе громкая синтетика.
 */
export function playTimeToPrepareSound() {
  void playAudioFileOneShot(SOUND_TIME_TO_PREPARE, 1, playSyntheticTimeToPrepareSound);
}

/**
 * Ранняя подгрузка звуков (после первого жеста пользователя можно вызвать из приложения).
 */
export function preloadNotificationSounds() {
  getPreloadedAudio(SOUND_NEW_ORDER);
  getPreloadedAudio(SOUND_TIME_TO_PREPARE);
}

/**
 * Показать системное уведомление «пора готовить заказ».
 * @param {{ id: number, estimated_ready_time?: string }} order
 */
export function showTimeToPrepareNotification(order) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }
  const title = `Пора готовить заказ #${orderDisplayNumber(order)}`;
  const body = 'До времени готовности осталось 5 минут';

  try {
    const notification = new Notification(title, {
      body,
      tag: NOTIFICATION_TAG_PREPARE,
      requireInteraction: true,
      icon: '/favicon.ico',
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (e) {
    console.error('[Notifications] Show time-to-prepare notification error:', e);
  }
}
